import { useCallback } from 'react';
import { formatter } from '@repo/shared';
import { Download, FileSpreadsheet, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import useDownload from '@/hooks/use-download';
import { useFileDownload } from '@/hooks/use-file-download';

interface FileUploadProps {
  id: string;
  name: string;
  size: number;
  onRemove?: () => void;
  download?: boolean;
}

const FileUpload = ({
  id,
  name,
  size,
  onRemove,
  download: hasDownload,
}: FileUploadProps) => {
  const { downloadFile } = useFileDownload({
    mutationKey: id,
    path: `/api/files/${id}/download`,
  });
  const { download } = useDownload();

  const handleDownload = useCallback(async () => {
    const { url, fileName } = await downloadFile();
    download({
      url,
      file: fileName,
    });
  }, [download, downloadFile]);

  return (
    <div className='bg-muted grid w-full grid-cols-[minmax(40px,40px)_1fr_minmax(32px,32px)] gap-1 rounded-lg p-2'>
      <span className='bg-background ring-input flex h-10 w-10 items-center justify-center rounded-sm shadow-sm ring-1 ring-inset'>
        <FileSpreadsheet
          className='text-foreground size-5'
          aria-hidden={true}
        />
      </span>
      <div className='truncate'>
        <p className='text-foreground text-xs font-medium'>{name}</p>
        <p className='text-muted-foreground mt-0.5 flex justify-between text-xs'>
          <span>{formatter.bytes.format(size)}</span>
        </p>
      </div>
      <div>
        {onRemove && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={onRemove}>
            <Button.Icon icon>
              <X />
            </Button.Icon>
          </Button>
        )}
        {hasDownload && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={handleDownload}>
            <Button.Icon icon>
              <Download />
            </Button.Icon>
          </Button>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
